(ns arthur.demo.stage
  "A saved take placed seven times on a stage. The EDN is the authored layout."
  (:require [arthur.domain.channel :as ch]
            [cljs.reader :as reader]
            [shadow.resource :as rc]))

(def layout (reader/read-string (rc/inline "arthur/demo/stage_8625.edn")))

(defn- position-track
  "Where the peg sits over time: its `center` plus a slow two-axis drift. The
  peg's own position, so the stage point the face is pinned to is what moves —
  not an offset that has to be kept in step with a changing scale."
  [center drift phase frames]
  (let [base center
        [dx dy] drift
        wave (fn [f period] (js/Math.sin (* 2 js/Math.PI (/ (+ f phase) period))))
        x0 (wave 0 96)
        y0 (wave 0 132)]
    (ch/keyed
     (into {}
           (for [f (conj (vec (range 0 frames 20)) (dec frames))]
             [f [(+ (first base) (* dx (- (wave f 96) x0)))
                 (+ (second base) (* dy (- (wave f 132) y0)))]]))
     :linear)))

(defn compose
  "The authored layout plus a source clip -> the composed stage document.

A PEG'S IDENTITY IS AUTHORED TOO, beside its instance's, for the reason the
  instance's is: `compose` is a pure function of the layout, so a generated one
  would make the same stage a different document on every call.

  AN INSTANCE IS KEYED BY ITS :uuid, not by the authored id. The authored id
  (`:left`, `:voice-right`) is a handle for reading the EDN and for the
  `:linked-to` written there; it does not appear in the document this returns.
  What replaces it is an identity that means one instance and nothing else: seven
  instances of one symbol are seven different things to name — to export on their
  own, to link a voice to, to point at later — and an id like `:left` is a
  description of where a thing sits, which is exactly what changes when the stage
  is re-arranged. `:name` carries the label for a human and `:source :symbol`
  carries the symbol, so the node still says what it is and which drawing it
  plays — and `:playback` says how time runs inside it, which is a separate
  question from which drawing that is."
  [source]
  (let [{:keys [name width height frames symbol instances audio scale]} layout
        ;; Which point of the SOURCE is pinned to the stage `:center`. Its
        ;; middle, unless the layout says otherwise for an off-centre drawing.
        default-origin (or (:origin layout)
                           [(/ (:width source) 2) (/ (:height source) 2)])
        original (get-in source [:symbols :main])
        ;; Authored id -> uuid, so the `:linked-to` in the EDN resolves to the
        ;; identity the document uses. Built before either pass because the audio
        ;; nodes refer to the instances.
        by-id (into {} (map (juxt :id :uuid)) (concat instances audio))
        uuid-of (fn [what id]
                  (or (get by-id id)
                      (throw (ex-info "the stage layout names an instance that is not there"
                                      {:in what :id id
                                       :known (vec (sort-by str (keys by-id)))}))))
        ;; EVERY PLACEMENT IS A PEG AND AN INSTANCE, and this layout is what
        ;; makes the pair necessary rather than tidy. `:scale` is KEYED — the
        ;; faces pulse — and it has to happen about the point the face is pinned
        ;; to. A stored `[:xform :anchor]` used to buy that: a static position
        ;; and a moving scale, turning about a fixed point. No static position
        ;; can do it alone, because `T(pos)·S(k(f))` moves the source's middle
        ;; whenever `k` changes, so holding it still would mean keying `pos` in
        ;; lockstep with `scale` — two channels that have to agree frame for
        ;; frame, which is the thing channels exist to avoid.
        ;;
        ;; A peg does it with neither:
        ;;
        ;;     peg       pos = center (+ drift),  scale = k(f)
        ;;       └ face  pos = -origin
        ;;
        ;;     world = T(center)·S(k(f))·T(-origin)
        ;;
        ;; which takes `origin` to `center` for EVERY k, with nothing keyed that
        ;; was not keyed before. That is the same matrix the anchor produced —
        ;; `node-test` asserts the identity — and it is reachable, keyable and
        ;; selectable, which the anchor was not.
        ;;
        ;; THE PEG IS THE PLACEMENT: it carries WHERE (pos, scale) and WHEN
        ;; (`:at`, `:span`), and the face under it carries only which drawing and
        ;; the offset to its origin. The time map has to be the peg's, because
        ;; `:scale` is read in the placement's own frames — that is what staggers
        ;; the entrances' growth — and `:span` goes with it so that
        ;; `node/placed-span` still answers where the placement sits on the stage.
        ;; The face then reads its peg's frames as its own and shows whenever the
        ;; peg does.
        nodes (into
               {:root {:id :root :name "stage" :kind :group :z "a1"}}
               (mapcat (fn [{:keys [uuid peg name z span at center origin drift phase]}]
                         (let [origin (or origin default-origin)]
                           [[peg {:id peg :name name :kind :group
                                  :parent :root :z z :span span
                                  :time {:mode :map :at at :rate 1}
                                  :channels {[:xform :pos]
                                             (if drift
                                               (position-track center drift phase frames)
                                               (ch/framed center))
                                             [:xform :scale] scale}}]
                            [uuid {:id uuid :name (str name " face") :kind :instance
                                   :parent peg :z "a1"
                                   :source {:symbol symbol}
                                   :channels {[:xform :pos]
                                              (ch/framed (mapv - origin))}}]]))
                       instances))
        nodes (into nodes
                    (map (fn [{:keys [uuid linked-to z source span at gain pan]}]
                           [uuid {:id uuid :kind :audio :parent :root :z z
                                  :linked-to (uuid-of uuid linked-to)
                                  :source source :span span
                                  :time {:mode :map :at at :rate 1}
                                  :channels (cond-> {[:audio :gain] gain}
                                              pan (assoc [:audio :pan] pan))}])
                         audio))]
    (assoc source :name name :width width :height height
           :symbols (assoc (:symbols source)
                             :main {:id :main :frames frames :nodes nodes}
                             symbol (assoc original :id symbol)))))
